import { TypographyCaption } from '@heidi/design-system/typography';
import { useIntl, type IntlShape } from 'react-intl';
import { LabelledContextPanel } from '../../inline-approval/labelled-context-panel';

import type { MergeApprovalFields } from './ApprovalRequestBlock.display';

/**
 * Clinician-friendly body for the ``merge_patient_profiles`` HITL approval
 * card. Replaces the raw ``tool_input`` JSON dump with three readable parts:
 *
 *   1. A heads-up banner stating plainly what the merge does and that it is
 *      reversible — the duplicate is archived with an audit trail, not
 *      permanently deleted.
 *   2. A "Profiles" panel making the merge direction explicit — which profile
 *      is kept vs which duplicate is removed (the safety-critical check). Ids
 *      are shown truncated in monospace for audit, enriched with the agent's
 *      corrected demographic label when present.
 *   3. A "Details saved to the kept profile" panel listing the reconciled
 *      clinical fields (medications, allergies, PMH, context) as plain text,
 *      or a calm note when the merge only moves sessions.
 */
export interface MergePatientProfilesApprovalDetailsProps {
  fields: MergeApprovalFields;
}

const shortId = (id: string | undefined): string => (id ? `…${id.slice(-8)}` : '—');

const reconciledFieldLabel = (intl: IntlShape, key: string): string => {
  switch (key) {
    case 'current_medications':
      return intl.formatMessage({
        defaultMessage: 'Current medications',
        id: 'WYquwaFZfT',
      });
    case 'allergies':
      return intl.formatMessage({ defaultMessage: 'Allergies', id: 'mWM48NqfRS' });
    case 'past_medical_history':
      return intl.formatMessage({
        defaultMessage: 'Past medical history',
        id: 'bL9ArTn3bc',
      });
    case 'additional_context':
      return intl.formatMessage({
        defaultMessage: 'Additional context',
        id: 'x9S/k+YsvF',
      });
    default:
      return key;
  }
};

export const MergePatientProfilesApprovalDetails = ({
  fields,
}: MergePatientProfilesApprovalDetailsProps) => {
  const intl = useIntl();
  const { demographicString, reconciled, targetId, sourceId } = fields;

  return (
    <div className="flex flex-col gap-3" data-testid="merge-patient-profiles-details">
      <p className="rounded-md border border-validation-warning/30 bg-validation-warning/10 px-3 py-2 text-xs leading-snug text-text-primary">
        {intl.formatMessage({
          defaultMessage:
            'All sessions and documents move onto the profile you keep. The duplicate is archived (not permanently deleted) and the merge is logged, so it can be reversed later if needed.',
          id: 'r60JE2NtGU',
        })}
      </p>

      <LabelledContextPanel
        label={intl.formatMessage({ defaultMessage: 'Profiles', id: '2zJXeApOic' })}
      >
        <dl className="flex flex-col gap-2">
          <div className="flex flex-col gap-0.5">
            <dt>
              <TypographyCaption className="font-medium text-text-secondary">
                {intl.formatMessage({ defaultMessage: 'Keeping', id: 'QhAdsfL7W/' })}
              </TypographyCaption>
            </dt>
            <dd className="break-words text-sm leading-snug text-text-primary [overflow-wrap:anywhere]">
              {demographicString ??
                intl.formatMessage({
                  defaultMessage: 'Canonical profile',
                  id: 'Ti2chyRd6W',
                })}
              <span className="ms-1.5 font-mono text-xs text-text-secondary">
                {shortId(targetId)}
              </span>
            </dd>
          </div>
          <div className="flex flex-col gap-0.5">
            <dt>
              <TypographyCaption className="font-medium text-text-secondary">
                {intl.formatMessage({
                  defaultMessage: 'Removing duplicate',
                  id: 'BRMqdpk/+K',
                })}
              </TypographyCaption>
            </dt>
            <dd className="font-mono text-xs leading-snug text-text-secondary">
              {shortId(sourceId)}
            </dd>
          </div>
        </dl>
      </LabelledContextPanel>

      <LabelledContextPanel
        label={intl.formatMessage({
          defaultMessage: 'Details saved to the kept profile',
          id: '4EOXv/tUiF',
        })}
      >
        {reconciled.length > 0 ? (
          <dl className="flex flex-col gap-2">
            {reconciled.map((field) => (
              <div key={field.key} className="flex flex-col gap-0.5">
                <dt>
                  <TypographyCaption className="font-medium text-text-secondary">
                    {reconciledFieldLabel(intl, field.key)}
                  </TypographyCaption>
                </dt>
                <dd className="whitespace-pre-wrap break-words text-sm leading-snug text-text-primary [overflow-wrap:anywhere]">
                  {field.value}
                </dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className="text-sm leading-snug text-text-secondary">
            {intl.formatMessage({
              defaultMessage:
                "No clinical details change — only the duplicate's sessions move across.",
              id: 'jqMIrI9hNt',
            })}
          </p>
        )}
      </LabelledContextPanel>
    </div>
  );
};
